import { UAParser } from "ua-parser-js";

export type UserAgentData = {
  ua_device: string | null;
  ua_os: string | null;
  ua_browser: string | null;
  ua_in_app: string | null;
};

// In-app browsers, checked in this order (Instagram's user agent can also carry
// Facebook's tokens).
const IN_APP_BROWSERS: Array<[string, RegExp]> = [
  ["instagram", /Instagram/i],
  ["facebook", /FBAN\/|FBAV\/|FB_IAB|FBIOS/i],
  ["google_app", /\bGSA\//i],
  // Android System WebView, or an iOS WebKit view without Safari's token.
  ["webview", /; wv\)|(iPhone|iPad|iPod)(?!.*Safari\/).*AppleWebKit/i],
];

/** facebook / instagram / google_app / webview, or null for a regular browser. */
export const detectInAppBrowser = (userAgent: string): string | null =>
  IN_APP_BROWSERS.find(([, pattern]) => pattern.test(userAgent))?.[0] ?? null;

/**
 * Device/OS/browser from the user agent, under ua_* keys; utm_device is reserved for
 * the ad platform's URL value. Never throws, so it can't fail signup or checkout.
 */
export const getUserAgentData = (parser?: UAParser): UserAgentData => {
  try {
    parser = parser ?? new UAParser();
    const os = parser.getOS();
    const browser = parser.getBrowser();
    return {
      ua_device: parser.getDevice().model || "desktop",
      ua_os: [os.name, os.version].filter(Boolean).join(" ") || null,
      ua_browser:
        [browser.name, browser.version].filter(Boolean).join(" ") || null,
      ua_in_app: detectInAppBrowser(parser.getUA() || ""),
    };
  } catch {
    // Keep the keys: the API reads a payload without ua_device as an older bundle.
    return { ua_device: null, ua_os: null, ua_browser: null, ua_in_app: null };
  }
};
